import React, { useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Upload, Image, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ETransferPage = () => {
  const location = useLocation();
  const data = location.state;
  const [proofImage, setProofImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setProofImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setProofImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmitProof = async () => {
    if (!proofImage) {
      toast.error('Please upload a screenshot of your payment');
      return;
    }

    setSubmitting(true);
    try {
      // Convert image to base64 for submission
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        await axios.post(`${API}/etransfer/proof`, {
          order_id: data.order_id,
          proof_image_url: base64Image,
          sender_name: senderName,
          notes: notes
        });
        
        setSubmitted(true);
        toast.success('Payment proof submitted successfully!');
      };
      reader.readAsDataURL(proofImage);
    } catch (e) {
      toast.error('Failed to submit proof. Please try again.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const clearImage = () => {
    setProofImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-neutral-500">No order data found</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="etransfer-submitted">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-500" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>
            Proof Submitted!
          </h1>
          <p className="text-neutral-400 mb-8">
            Thank you! Your payment proof has been submitted for review. 
            You'll receive confirmation within 24 hours once verified.
          </p>
          <Link to="/" className="btn-primary inline-block">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="etransfer-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Order Placed!</h1>
          <p className="text-neutral-400">Please complete your payment via e-transfer</p>
        </div>

        <div className="glass-card p-8 mb-8">
          <h2 className="text-xl font-bold mb-6 text-white" style={{ fontFamily: 'Syne' }}>E-Transfer Instructions</h2>
          
          <div className="space-y-4 mb-8">
            <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
              <span className="text-neutral-400">Order ID</span>
              <span className="font-mono text-white">{data.order_id}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
              <span className="text-neutral-400">Amount</span>
              <div>
                <span className="font-bold text-xl gradient-text">${data.total?.toFixed(2)}</span>
                <span className="text-neutral-500 ml-2">CAD</span>
              </div>
            </div>
            <div className="flex justify-between py-3 border-b border-[#D4AF37]/10">
              <span className="text-neutral-400">Send to</span>
              <span className="text-[#D4AF37] font-bold">{data.etransfer_info?.email}</span>
            </div>
          </div>

          <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-6 mb-8">
            <p className="text-neutral-300">{data.etransfer_info?.instructions}</p>
          </div>

          {/* Upload Proof Section */}
          <div className="border-t border-[#D4AF37]/10 pt-8">
            <h3 className="text-lg font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>
              Upload Payment Proof
            </h3>
            <p className="text-neutral-400 text-sm mb-6">
              After sending your e-transfer, upload a screenshot of your payment confirmation below.
            </p>

            {/* File Upload Area */}
            <div
              className="file-upload-area mb-6"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="proof-upload-input"
              />
              
              {previewUrl ? (
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Payment proof" 
                    className="max-h-64 mx-auto rounded-lg"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); clearImage(); }}
                    className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors"
                  >
                    <X size={16} className="text-white" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={48} className="mx-auto mb-4 text-[#D4AF37]/50" />
                  <p className="text-neutral-400 mb-2">
                    Drag & drop your screenshot here, or click to browse
                  </p>
                  <p className="text-neutral-500 text-sm">PNG, JPG up to 5MB</p>
                </>
              )}
            </div>

            {/* Optional Fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Your Name (optional)</label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white"
                  data-testid="sender-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  rows={3}
                  className="w-full bg-neutral-900/50 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white resize-none"
                  data-testid="notes-input"
                />
              </div>
            </div>

            <button
              onClick={handleSubmitProof}
              disabled={!proofImage || submitting}
              className={`btn-primary w-full flex items-center justify-center gap-2 ${!proofImage ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="submit-proof-btn"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Image size={18} />
                  Submit Payment Proof
                </>
              )}
            </button>
          </div>
        </div>

        <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
          <p className="text-yellow-500 text-sm">
            Your files will be delivered within 24 hours of payment verification.
          </p>
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="text-neutral-400 hover:text-[#D4AF37] transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ETransferPage;
